// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

/** One `{ id, value, display }` option — `value` is the technical name (URLs/params), `display` the label. */
export type BackofficeContextPair = components["schemas"]["ContextEntryWithIdResponse"];

/** The full `/v1/context` payload, derived from the OpenAPI schema so new fields flow in via codegen. */
export type BackofficeContext = components["schemas"]["ContextResponse"];

/**
 * Sort chains, stores and per-chain regions alphabetically by their display
 * label so every consumer (data-table filters, dialog pickers, …) presents a
 * consistently ordered list without each call site having to re-sort.
 */
function sortContext(ctx: BackofficeContext): BackofficeContext {
  const byDisplay = (a: BackofficeContextPair, b: BackofficeContextPair) => a.display.localeCompare(b.display);
  return {
    ...ctx,
    chains: [...(ctx.chains ?? [])].sort(byDisplay),
    stores: [...(ctx.stores ?? [])].sort(byDisplay),
    regionsByChain: Object.fromEntries(
      Object.entries(ctx.regionsByChain ?? {}).map(([chain, regions]) => [
        chain,
        [...regions].sort((a, b) => a.localeCompare(b)),
      ]),
    ),
  };
}

export function useBackofficeContext() {
  return useQuery({
    queryKey: ["backoffice", "context"],
    queryFn: async () => {
      const res = await api.GET("/v1/context");
      return sortContext(res.data as BackofficeContext);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - context data changes rarely
  });
}
